import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Zap,
  Loader2,
  CheckCircle,
  Shield,
  Smartphone,
  ArrowRight,

  Download,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickBuyModal({ product, open, onOpenChange }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | paying | success | failed
  const [loading, setLoading] = useState(false);
  const [transactionRef, setTransactionRef] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS" }).format(price);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all fields");
      return;
    }

    const phoneRegex = /^(\+233[235]\d{8}|0[235]\d{8})$/;
    if (!phoneRegex.test(form.phone.replace(/\s/g, ""))) {
      toast.error("Enter a valid Ghana number: 05X XXX XXXX or +233 5X XXX XXXX");
      return;
    }

    setLoading(true);
    try {
      // 1. Create the order
      const checkoutResponse = await axios.post(`${API}/checkout`, {
        items: [{ product_id: product.id, quantity: 1 }],
        customer_email: form.email,
        customer_phone: form.phone.replace(/\s/g, ""),
        customer_name: form.name,
        create_account: false,
        password: null,
        promo_code: null,
      });

      const { transaction_ref } = checkoutResponse.data;
      setTransactionRef(transaction_ref);

      // 2. Get payment config
      const paymentResponse = await axios.post(`${API}/payment/initiate`, {
        transaction_ref,
      });

      const { mock, checkout_url } = paymentResponse.data;

      if (mock) {
        toast.info("Using mock payment for development");
        sessionStorage.setItem("pending_transaction", transaction_ref);
        window.location.href = `/payment/mock?ref=${transaction_ref}`;
        return;
      }

      // 3. Redirect to Hubtel checkout page
      sessionStorage.setItem("pending_transaction", transaction_ref);
      window.location.href = checkout_url;

    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed. Please try again.");
      setStep("form");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setStep("form");
    setTransactionRef(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="quick-buy-modal">
        {/* Product Header */}
        <div className="relative bg-navy-900 text-white p-6">
          <div className="absolute inset-0 opacity-20 bg-gradient-to-br from-accent/30 to-transparent" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-medium text-yellow-400">
                {step === "success" ? "Purchase Complete" : step === "paying" ? "Processing Payment" : "Quick Buy"}
              </span>
            </div>
            <h3 className="font-bold text-lg line-clamp-2 mb-1">{product.title}</h3>
            <div className="flex items-center gap-3 text-sm text-gray-300">
              <span>{product.subject}</span>
              <span>·</span>
              <span>{product.class_level}</span>
            </div>
            <p className="text-2xl font-bold text-accent mt-3">
              {formatPrice(product.price_ghs)}
            </p>
          </div>
        </div>

        {/* Step: Success */}
        {step === "success" && (
          <div className="p-6 text-center space-y-4" data-testid="payment-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-navy-900">Payment Successful!</h3>
            <p className="text-gray-500 text-sm">Your mock exam is ready for download. Check your email for the order confirmation.</p>
            <Button
              onClick={() => navigate(`/payment/success?ref=${transactionRef}`)}
              className="w-full h-12 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
              data-testid="go-to-download"
            >
              <Download className="w-5 h-5 mr-2" /> Download Now
            </Button>
          </div>
        )}

        {/* Step: Failed */}
        {step === "failed" && (
          <div className="p-6 text-center space-y-4" data-testid="payment-failed">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-navy-900">Payment Failed</h3>
            <p className="text-gray-500 text-sm">Something went wrong with your payment. Please try again.</p>
            <Button
              onClick={() => setStep("form")}
              className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-bold"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Step: Paying (Hubtel SDK handles UI) */}
        {step === "paying" && (
          <div className="p-6 text-center space-y-4" data-testid="payment-processing">
            <Loader2 className="w-10 h-10 animate-spin text-accent mx-auto" />
            <p className="text-gray-500">Hubtel payment window is open. Complete your payment there.</p>
          </div>
        )}

        {/* Step: Form */}
        {step === "form" && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <AnimatePresence mode="wait">
              <motion.div key="form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {isAuthenticated ? (
                  <div className="flex items-center gap-3 bg-green-50 text-green-700 p-3 rounded-xl text-sm">
                    <CheckCircle className="w-4 h-4 shrink-0" />
                    <span>Buying as <strong>{user.email}</strong></span>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Kwame Asante" required className="rounded-xl h-11" data-testid="quick-buy-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="kwame@example.com" required className="rounded-xl h-11" data-testid="quick-buy-email" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Mobile Money Number</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d+]/g, "");
                      const sanitized = val.charAt(0) === "+" ? "+" + val.slice(1).replace(/\+/g, "") : val.replace(/\+/g, "");
                      setForm((p) => ({ ...p, phone: sanitized }));
                    }}
                    placeholder="0241234567 or +233241234567"
                    required
                    type="tel"
                    inputMode="tel"
                    maxLength={13}
                    className={`rounded-xl h-11 ${form.phone && !/^(\+233[235]\d{8}|0[235]\d{8})$/.test(form.phone.replace(/\s/g, "")) ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                    data-testid="quick-buy-phone"
                  />
                  {form.phone && !/^(\+233[235]\d{8}|0[235]\d{8})$/.test(form.phone.replace(/\s/g, "")) ? (
                    <p className="text-xs text-red-500">Enter a valid Ghana number: 05X XXX XXXX or +233 5X XXX XXXX</p>
                  ) : (
                    <p className="text-xs text-gray-500">MTN MoMo, Vodafone Cash, or AirtelTigo</p>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">1x {product.title}</span>
                <span className="font-medium">{formatPrice(product.price_ghs)}</span>
              </div>
              <div className="border-t pt-2 flex items-center justify-between font-bold">
                <span>Total</span>
                <span className="text-accent text-lg">{formatPrice(product.price_ghs)}</span>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-bold text-base shadow-lg shadow-accent/25 group" data-testid="quick-buy-submit">
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Smartphone className="w-5 h-5 mr-2" />
                  Pay {formatPrice(product.price_ghs)} via Mobile Money
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-4 text-xs text-gray-400 pt-1">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure</span>
              <span className="flex items-center gap-1"><img src="/logo.png" alt="GMP" className="w-3 h-3 object-contain" /> Instant Download</span>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
